import React, { useEffect, useState } from "react";
import { Plus, Printer, Trash2, Tag, Package } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

/* Label Maker , dahlia variety stickers. Pick one of the 3 artwork
   backgrounds, choose a catalogue variety or type any text, queue labels,
   print at exactly 8cm x 8cm with the name overlaid in white serif. */

const ARTS = [
  { id: "bg1", src: "/label-art/bg1.png", name: "Autumn Blush" },
  { id: "bg2", src: "/label-art/bg2.png", name: "Magenta Dusk" },
  { id: "bg3", src: "/label-art/bg3.png", name: "Golden Morning" },
];

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export default function AdminLabelMaker() {
  const [catalog, setCatalog] = useState([]);
  const [art, setArt] = useState(ARTS[0]);
  const [text, setText] = useState("");
  const [queue, setQueue] = useState([]);
  const [size, setSize] = useState(8);
  const [batching, setBatching] = useState(false);

  useEffect(() => {
    api.get("/admin/catalog").then((r) => setCatalog(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const add = () => {
    const t = text.trim();
    if (!t) { toast.error("Pick a variety or type some text first."); return; }
    setQueue((q) => [...q, { id: Date.now(), art, text: t }]);
    setText("");
  };

  const batchFromAudit = async () => {
    setBatching(true);
    try {
      const r = await api.get("/admin/tuber-audit");
      const names = (r.data?.rows || []).map((row) => (row.name || "").trim()).filter(Boolean);
      if (names.length === 0) { toast.error("Your Tuber Audit is empty , count some varieties first."); return; }
      setQueue((q) => {
        const have = new Set(q.map((l) => l.text.toLowerCase()));
        const fresh = names.filter((n) => !have.has(n.toLowerCase()));
        toast.success(`${fresh.length} label${fresh.length === 1 ? "" : "s"} queued from your Tuber Audit${fresh.length < names.length ? ` (${names.length - fresh.length} already queued)` : ""}.`);
        return [...q, ...fresh.map((n, i) => ({ id: Date.now() + i, art, text: n }))];
      });
    } catch { toast.error("Couldn't load the Tuber Audit."); }
    finally { setBatching(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-label-maker">
      <header>
        <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Variety stickers</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-1 text-[#3C2A1E]">Label Maker</h1>
        <p className="text-sm italic text-[#7A6A55] mt-1">Pick an artwork, choose a variety (or write anything), queue as many as you need, print at 8cm × 8cm.</p>
      </header>

      <div className="bg-white border border-[#E8DFCB] rounded p-4 space-y-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] mb-2 inline-flex items-center gap-1.5"><Tag className="w-4 h-4" /> Artwork</div>
          <div className="flex gap-3">
            {ARTS.map((a) => (
              <button key={a.id} type="button" onClick={() => setArt(a)} data-testid={`label-art-${a.id}`}
                className={`relative w-28 h-28 rounded bg-[#2A1A2E] p-1.5 border-2 transition-colors ${art.id === a.id ? "border-[#6E1E3A]" : "border-transparent hover:border-[#C49247]"}`}>
                <img src={a.src} alt={a.name} className="w-full h-full object-contain" />
                <span className="absolute bottom-1 inset-x-0 text-center text-[9px] tracking-[0.15em] uppercase font-bold text-white/85">{a.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input list="label-maker-catalog" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Search the catalogue or type your own text…" data-testid="label-maker-text"
            className="flex-1 min-w-[240px] h-11 px-3 border border-[#E8DFCB] rounded text-sm bg-white outline-none focus:border-[#6E1E3A]" />
          <button type="button" onClick={add} data-testid="label-maker-add"
            className="h-11 px-5 bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Add label
          </button>
          <button type="button" onClick={batchFromAudit} disabled={batching} data-testid="label-maker-batch-audit"
            className="h-11 px-5 border border-[#2F6B4F] text-[#2F6B4F] bg-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            title="Queue one label for every variety on your current Tuber Audit">
            <Package className="w-4 h-4" /> Queue from audit
          </button>
          <div className="inline-flex items-center rounded border border-[#E8DFCB] bg-white overflow-hidden" data-testid="label-size-picker">
            {[6, 8, 10].map((s) => (
              <button key={s} type="button" onClick={() => setSize(s)} data-testid={`label-size-${s}`}
                className={`h-11 px-4 text-xs font-bold transition-colors ${size === s ? "bg-[#6E1E3A] text-white" : "text-[#3C2A1E] hover:bg-[#FBF6E9]"}`}>
                {s}cm
              </button>
            ))}
          </div>
          <button type="button" onClick={() => printLabels(queue, size)} disabled={queue.length === 0} data-testid="label-maker-print"
            className="h-11 px-5 border border-[#6E1E3A] text-[#6E1E3A] bg-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
            <Printer className="w-4 h-4" /> Print {queue.length || ""} label{queue.length === 1 ? "" : "s"} ({size}×{size}cm)
          </button>
        </div>
        <datalist id="label-maker-catalog">{catalog.map((v) => <option key={v.id} value={v.name} />)}</datalist>
      </div>

      {queue.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="label-maker-queue">
          {queue.map((l) => (
            <div key={l.id} className="relative bg-[#2A1A2E] rounded p-2 group">
              <LabelPreview art={l.art} text={l.text} />
              <button type="button" onClick={() => setQueue((q) => q.filter((x) => x.id !== l.id))} data-testid={`label-remove-${l.id}`}
                className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/90 text-[#A33030] opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const lines = (t) => t.trim().split(/\s+/);
const fontFor = (t, box) => {
  const longest = Math.max(...lines(t).map((w) => w.length), 1);
  return Math.min(box * 0.22, (box * 1.55) / longest);
};

function LabelPreview({ art, text }) {
  const size = 150;
  const fs = fontFor(text, size);
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <img src={art.src} alt="" className="absolute inset-0 w-full h-full object-contain" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3"
        style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#fff", textShadow: "0 1px 6px rgba(0,0,0,.45)", lineHeight: 1.02 }}>
        {lines(text).map((w, i) => <div key={i} style={{ fontSize: fs }}>{w}</div>)}
      </div>
    </div>
  );
}

function printLabels(queue, size = 8) {
  const mm = size * 10;
  const px = mm * 3.78; // css mm → px at 96dpi
  const cols = size === 6 ? 3 : 2;
  const gap = size === 10 ? 0 : 5;
  const cells = queue.map((l) => {
    const fs = fontFor(l.text, px);
    return `
    <div class="cell">
      <img src="${l.art.src}" alt=""/>
      <div class="txt" style="font-size:${fs}px;">${lines(l.text).map((w) => `<div>${esc(w)}</div>`).join("")}</div>
    </div>`;
  }).join("");
  const html = `<!doctype html><html><head><title>Dahlia labels ${size}cm</title>
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600&display=swap" rel="stylesheet"/>
  <style>
    @page{size:A4;margin:0;}
    body{margin:5mm;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .grid{display:grid;grid-template-columns:repeat(${cols}, ${mm}mm);gap:${gap}mm;}
    .cell{position:relative;width:${mm}mm;height:${mm}mm;page-break-inside:avoid;}
    .cell img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;}
    .txt{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;
      font-family:'Playfair Display',Georgia,serif;font-weight:500;color:#fff;line-height:1.02;padding:0 ${size + 2}mm;
      text-shadow:0 1px 8px rgba(0,0,0,.5);}
    @media print{.noprint{display:none;}}
  </style></head><body>
    <div class="noprint" style="text-align:right;margin-bottom:6px"><button onclick="window.print()" style="padding:8px 18px;cursor:pointer;">Print</button></div>
    <div class="grid">${cells}</div>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}
